import { useId, useState, type FormEvent } from 'react';
import { Building2 } from 'lucide-react';
import type { Tenant } from '../../types/lac';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksPhotoUpload } from '../../components/liks/LiksPhotoUpload/LiksPhotoUpload';
import { LiksTextFormField } from '../../components/liks/LiksTextFormField/LiksTextFormField';
import { useUpdateLacTenantMutation } from '../../features/lac/mutations/useLacTenantMutations';
import { useI18n } from '../../i18n/useI18n';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import type { LacUpdateTenantInput } from '../../types/lac';
import { toPhotoPayload } from '../../utils/imageUtils';
import {
  DESCRIPTION_MAX_LENGTH,
  initialDescription,
  initialDisplayName,
  initialMaxUsers,
} from './lacTenantDetailsFormInitial';
import { useLacTenantDetailsLogo } from './useLacTenantDetailsLogo';
import './LacTenantDetailsPage.css';

export type LacTenantDetailsFormProps = {
  tenant: Tenant;
  tenantName: string;
};

export function LacTenantDetailsForm({
  tenant,
  tenantName,
}: LacTenantDetailsFormProps) {
  const { translate } = useI18n();
  const updateTenantMutation = useUpdateLacTenantMutation();
  const logoFieldId = useId();
  const {
    logo,
    logoTouched,
    isLogoHydrating,
    logoError,
    logoDisplaySrc,
    handleLogoChange,
    handleRemoveLogo,
    resetLogo,
  } = useLacTenantDetailsLogo(tenant);

  const [displayName, setDisplayName] = useState(() =>
    initialDisplayName(tenant),
  );
  const [description, setDescription] = useState(() =>
    initialDescription(tenant),
  );
  const [maxUsers, setMaxUsers] = useState(() => initialMaxUsers(tenant));
  const [maxChatbots, setMaxChatbots] = useState(
    tenant.maxChatbots != null ? String(tenant.maxChatbots) : '',
  );

  const isSubmitting = updateTenantMutation.isPending;

  const trimmedDisplayName = displayName.trim();
  const trimmedDescription = description.trim();
  const isDescriptionTooLong =
    trimmedDescription.length > DESCRIPTION_MAX_LENGTH;

  const trimmedMaxUsers = maxUsers.trim();
  const parsedMaxUsers =
    trimmedMaxUsers === '' ? null : Number(trimmedMaxUsers);
  const isMaxUsersInvalid =
    trimmedMaxUsers !== '' &&
    (!Number.isInteger(parsedMaxUsers) ||
      parsedMaxUsers == null ||
      parsedMaxUsers < 1);

  const isDirty =
    trimmedDisplayName !== initialDisplayName(tenant).trim() ||
    trimmedDescription !== initialDescription(tenant).trim() ||
    trimmedMaxUsers !== initialMaxUsers(tenant).trim() ||
    logoTouched;
  const trimmedMaxChatbots = maxChatbots.trim();
  const parsedMaxChatbots =
    trimmedMaxChatbots === '' ? null : Number(trimmedMaxChatbots);
  const isMaxChatbotsInvalid =
    trimmedMaxChatbots !== '' &&
    (!Number.isInteger(parsedMaxChatbots) ||
      parsedMaxChatbots == null ||
      parsedMaxChatbots < 1);

  const isSaveDisabled =
    !isDirty ||
    isSubmitting ||
    isLogoHydrating ||
    trimmedDisplayName === '' ||
    isDescriptionTooLong ||
    isMaxUsersInvalid ||
    isMaxChatbotsInvalid ||
    logoError != null;

  const handleCancel = () => {
    setDisplayName(initialDisplayName(tenant));
    setDescription(initialDescription(tenant));
    setMaxUsers(initialMaxUsers(tenant));
    resetLogo();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaveDisabled) {
      return;
    }

    const input: LacUpdateTenantInput = {
      tenantName,
      displayName: trimmedDisplayName,
      description: trimmedDescription,
      maxUsers: parsedMaxUsers,
      maxChatbots: parsedMaxChatbots,
      active: tenant.active,
      ...(logoTouched ? { logo: toPhotoPayload(logo) } : {}),
    };

    updateTenantMutation.mutate(input, {
      onSuccess: () => {
        toast.success({
          title: translate('lac.organisations.details.saveSuccessTitle'),
          description: translate(
            'lac.organisations.details.saveSuccessDescription',
            { name: input.displayName },
          ),
        });
      },
      onError: (error) => {
        void toastMutationError(
          error,
          translate('lac.organisations.details.saveErrorTitle'),
          translate('lac.organisations.details.saveErrorDescription'),
        );
      },
    });
  };

  return (
    <form className="lac-tenant-details__form" onSubmit={handleSubmit}>
      <div className="lac-tenant-details__card">
        <div className="lac-tenant-details__body">
          <div className="lac-tenant-details__fields">
            <div className="lac-tenant-details__row">
              <LiksTextFormField
                label={translate('lac.organisations.create.nameLabel')}
                required
                value={tenantName}
                disabled
                readOnly
              />

              <LiksTextFormField
                label={translate('lac.organisations.create.displayNameLabel')}
                required
                value={displayName}
                onChange={setDisplayName}
                disabled={isSubmitting}
                autoComplete="off"
              />
            </div>

            <LiksTextFormField
              label={translate('lac.organisations.create.descriptionLabel')}
              multiline
              value={description}
              onChange={setDescription}
              disabled={isSubmitting}
              autoComplete="off"
              error={
                isDescriptionTooLong
                  ? translate('lac.organisations.details.descriptionTooLong')
                  : null
              }
            />

            <div className="lac-tenant-details__row">
              <LiksTextFormField
                label={translate('lac.organisations.create.maxUsersLabel')}
                type="number"
                value={maxUsers}
                onChange={setMaxUsers}
                disabled={isSubmitting}
                autoComplete="off"
                min={1}
                step={1}
                error={
                  isMaxUsersInvalid
                    ? translate('lac.organisations.create.maxUsersInvalid')
                    : null
                }
              />

              <LiksTextFormField
                label={translate('lac.organisations.create.maxChatbotsLabel')}
                type="number"
                value={maxChatbots}
                onChange={setMaxChatbots}
                disabled={isSubmitting}
                autoComplete="off"
                min={1}
                step={1}
                error={
                  isMaxChatbotsInvalid
                    ? translate('lac.organisations.create.maxChatbotsInvalid')
                    : null
                }
              />
            </div>
          </div>

          <LiksPhotoUpload
            className="lac-tenant-details__logo"
            variant="tenant"
            src={logoDisplaySrc}
            isLoading={isLogoHydrating}
            placeholder={<Building2 strokeWidth={1.5} aria-hidden />}
            inputId={logoFieldId}
            disabled={isSubmitting}
            onFileChange={(event) => {
              void handleLogoChange(event);
            }}
            onRemove={handleRemoveLogo}
            error={logoError}
          />
        </div>

        <div className="lac-tenant-details__actions">
          {isDirty ? (
            <LiksButton
              type="button"
              variant="secondary"
              disabled={isSubmitting}
              onClick={handleCancel}
            >
              {translate('lac.organisations.details.cancel')}
            </LiksButton>
          ) : null}
          <LiksButton type="submit" disabled={isSaveDisabled}>
            {translate('lac.organisations.details.save')}
          </LiksButton>
        </div>
      </div>
    </form>
  );
}
